import React, { forwardRef } from 'react';
import { node, oneOf, string } from 'prop-types';
import { classesMap, presetsMap } from './text.config';

/**
 * Merges the provided classes and optional props into the preset defaults.
 *
 * @param {object} config - The preset's configuration.
 * @param {string} classNames - The classes provided in the `className` prop.
 * @param {array} options - The available props that can overide the preset's defaults.
 *
 * @returns {string} The merged classes.
 */
const mergeClasses = (config, classNames, options) => {
    const classes = classNames ? [classNames] : [];

    Object.values(config.constants).forEach((value) => classes.push(value));

    options.forEach(([prop, value]) => {
        if (value) classes.push(classesMap[prop][value]);
        else if (config[prop]) classes.push(config[prop]);
    });

    return classes.join(' ');
};

/**
 * Merges the provided classes and optional props into the preset defaults.
 *
 * @param {string} tag - An HTML tag.
 * @param {array} options - The allowed HTML tags.
 * @param {string} preset - The name of the preset for use in error message.
 *
 * @returns {string} A valid HTML tag.
 */
const getValidTag = (tag, options, preset) => {
    let validTag = options[0];

    if (!tag) return validTag;

    if (options.includes(tag)) validTag = tag;
    else {
        console.error(
            [
                'Error: Invalid prop `tag` of value "',
                tag,
                '" supplied to `<Text preset="',
                preset,
                '"/>`, expected one of ["',
                options.join("', '"),
                '"]. Falling back to `',
                validTag,
                '`.',
            ].join('')
        );
    }

    return validTag;
};

/**
 * Enables using presets to style text to conform to the styleguide.
 */
const Text = forwardRef((props, ref) => {
    const { automationId, children, align, className, preset, size, tag, ...attrs } = props;

    const presetObj = presetsMap[preset];
    const Tag = getValidTag(tag, presetObj.tags, preset);

    const classes = mergeClasses(
        presetObj,
        className,
        Object.entries({
            align,
            size,
        })
    );

    return (
        <Tag ref={ref} className={classes} {...attrs} data-automation-id={automationId}>
            {children}
        </Tag>
    );
});

Text.displayName = 'Text';

Text.propTypes = {
    /**
     * The node(s) to render in the component.
     */
    children: node.isRequired,

    /**
     * The data-automation-id to apply.
     */
    automationId: string,

    /**
     * Override the preset's text alignment.
     */
    align: oneOf(['center', 'justify', 'left', 'right']),

    /**
     * Extends the classes that are applied.
     */
    className: string,

    /**
     * Applies styles associated with the preset.
     */
    preset: oneOf([
        'body',
        'legal',
        'price',
        'promo-label',
        'promo-percent',
        'promo-was',
        'title',
        'title-pdp',
        'urgency',
    ]),

    /**
     * Override the preset's font-size.
     */
    size: oneOf(['xsmall', 'small', 'medium', 'large', 'xlarge']),

    /**
     * Override the preset's HTML tag.
     */
    tag: string,
};

Text.defaultProps = {
    align: null,
    automationId: null,
    className: null,
    preset: 'body',
    size: null,
    tag: null,
};

export default Text;
